// ============================================
// Appearance State — replaces AppearanceState.cs
// Resolves image/video variant names based on
// Ink boolean states (chastity, longhair, etc.)
// ============================================

import { events, Events } from '../engine/events.js';
import { IMAGE_FOLDERS } from '../engine/constants.js';
import { fetchWithEdgeRetry } from '../engine/story-fetch.js';

// Ordered list of appearance suffixes (most important first for bitmask ordering)
const SUFFIX_DEFINITIONS = [
    { inkVariable: 'wearing_chastity', imageSuffix: '_chastity' },
    { inkVariable: 'has_long_hair', imageSuffix: '_longhair' },
    { inkVariable: 'has_visible_breasts', imageSuffix: '_boobs' },
    // Masc axis — index 3 = HIGHEST bit, on purpose. The descending-submask
    // iteration in resolveVariantName then tries EVERY _masc combo
    // (_chastity_longhair_masc → _longhair_masc → _chastity_masc → _masc)
    // BEFORE any feminine variant. So a masc-presenting player in a scene that
    // only has _masc (no _masc_chastity yet) shows male clothes WITHOUT the
    // cage detail rather than degrading to a feminine skirt+cage render.
    // Mutually exclusive with _boobs (see _buildActiveMask — male hides chest).
    // Driven by presenting_masc (= !EquippedFeminine), read FRESH from Ink in
    // _buildActiveMask: the starter outfit equips before the ink pusher is
    // registered (character-editor), so _activeStates can't carry the
    // cold-start TRUE value the way the other 3 always-false-on-Day-1 axes can.
    { inkVariable: 'presenting_masc', imageSuffix: '_masc' },
];

// Image-name prefixes that should ALSO try a nest_tier suffix (`_nest1/2/3`)
// between the base name and the boolean suffixes. apartment_* and master_visit_*
// scenes change visually as the player buys decor (apartment_nest_tier 0-3).
// Plan §4.2: skip the boobs axis for these names (rare relevance), keeps the
// cross-product under the 8-variant cap.
const NEST_PREFIXES = ['apartment_', 'master_visit_'];

// Ordered-axis variant-tail grammar, derived from SUFFIX_DEFINITIONS so the
// suffix literals live in exactly one place. STRIP version (end-anchored only,
// for String.replace — earliest match wins, which is the LONGEST valid tail)
// and FULLMATCH version (anchored both ends, one capture group per axis, for
// parseVariantTail).
const VARIANT_TAIL_STRIP_RE = new RegExp(
    SUFFIX_DEFINITIONS.map((d) => `(?:${d.imageSuffix})?`).join('') + '$'
);
const VARIANT_TAIL_FULL_RE = new RegExp(
    '^' + SUFFIX_DEFINITIONS.map((d) => `(${d.imageSuffix})?`).join('') + '$'
);

class AppearanceState {
    constructor() {
        /** @type {boolean[]} active state for each suffix */
        this._activeStates = new Array(SUFFIX_DEFINITIONS.length).fill(false);

        /** @type {Map<string, string>} baseName -> resolved image variant */
        this._variantCache = new Map();

        /** @type {Map<string, string>} baseName -> resolved video variant */
        this._videoVariantCache = new Map();

        /** @type {Set<string>} known video filenames (from manifest) */
        this._knownVideos = null;

        /** @type {Set<string>} known image names (populated as discovered) */
        this._knownImages = new Set();

        /** @type {Set<string>} variant names known to be missing (avoids re-trying 404s) */
        this._variantMissCache = new Set();

        /** @type {Map<string, string>} image name -> resolved URL */
        this._imagePathCache = new Map();

        /** @type {function|null} callback to read Ink variables */
        this._inkReader = null;

        /** @type {boolean} debug overrides */
        this._debugForceChastity = false;
        this._debugForceLongHair = false;
        this._debugOverride = false;

        // Scoped listeners (F07 lifecycle rule — bare events.on escapes the
        // teardown path; deep-review L-10).
        this._scope = events.createScope('appearance');
        // Listen for variable changes that affect appearance
        this._scope.on(Events.VARIABLE_CHANGED, (name, value) => {
            this._onVariableChanged(name, value);
        });
        // Also listen to flag changes (boolean Ink vars like wearing_chastity, has_long_hair)
        this._scope.on(Events.FLAG_CHANGED, (name, value) => {
            this._onVariableChanged(name, value);
        });
        // The masc axis is read fresh from Ink per resolve, but resolved
        // variant NAMES are cached per base name. Equipping/unequipping flips
        // presenting_masc (and EquippedFeminine), so drop the cache on any
        // outfit change — the next resolve re-reads the live flag.
        this._scope.on(Events.ITEM_EQUIPPED, () => this.invalidateCache());
        this._scope.on(Events.ITEM_UNEQUIPPED, () => this.invalidateCache());
        this._scope.on(Events.BUFFS_RECALCULATED, () => this.invalidateCache());
    }

    /** Remove all event listeners (test/reset flows). */
    destroy() {
        try { this._scope?.destroy?.(); } catch (e) {}
    }

    /**
     * Register callback to read Ink variables.
     * Signature: (varName) => value
     */
    registerInkReader(fn) {
        this._inkReader = fn;
    }

    /**
     * Initialize state from Ink story variables.
     * Call after story is created/loaded.
     * @param {function} readVar - (varName) => value
     */
    initFromStory(readVar) {
        for (let i = 0; i < SUFFIX_DEFINITIONS.length; i++) {
            const val = readVar(SUFFIX_DEFINITIONS[i].inkVariable);
            this._activeStates[i] = this._toBool(val);
        }
        // Wig forces longhair (OR with has_long_hair at index 1)
        this._applyWigOverride(readVar);
        this.invalidateCache();
    }

    /**
     * Refresh state from story without re-registering observers.
     */
    refreshFromStory(readVar) {
        this.initFromStory(readVar);
    }

    // ---- Image variant resolution ----

    /**
     * Resolve the best available image variant for a base sprite name.
     * Returns the most specific variant that exists, falling back to base.
     * @param {string} baseName
     * @param {function} [exists] - (candidateName) => boolean
     * @returns {string} resolved variant name
     */
    resolveVariantName(baseName, exists) {
        if (!baseName) return baseName;

        const activeMask = this._buildActiveMask();
        const nestTier = this._getNestTierForName(baseName);
        // Cache key includes the live axis mask + nest tier: the feminine
        // axes are read fresh from Ink (salon/spa story writes emit no JS
        // event), so the same base name can legitimately resolve differently
        // between knots without any invalidateCache() in between.
        const cacheKey = `${baseName}|${activeMask}|${nestTier}`;
        const cached = this._variantCache.get(cacheKey);
        if (cached !== undefined) return cached;

        const checkExists = exists || ((c) => this._knownImages.has(c));

        // Outer loop: nest_tier from current down to 0 (only for apartment_/master_visit_ names)
        for (let tier = nestTier; tier >= 0; tier--) {
            const tieredBase = tier > 0 ? `${baseName}_nest${tier}` : baseName;
            const useDropBoobs = tier > 0;
            const mask = useDropBoobs ? this._maskWithoutBoobs(activeMask) : activeMask;

            // Inner loop: boolean suffix submasks (chastity, longhair, [boobs])
            for (let m = mask; m >= 0; m = m === 0 ? -1 : this._nextSubMask(m, mask)) {
                const candidate = this.buildVariantName(tieredBase, m);
                if (this._variantMissCache.has(candidate)) continue;
                if (checkExists(candidate)) {
                    this._variantCache.set(cacheKey, candidate);
                    return candidate;
                }
                if (m === 0) break;
            }
        }

        // Fallback to base name (already tried in the tier=0,mask=0 iteration)
        this._variantCache.set(cacheKey, baseName);
        return baseName;
    }

    /**
     * Resolve the best available video variant.
     */
    resolveVideoVariantName(baseName) {
        if (!baseName) return baseName;

        const activeMask = this._buildActiveMask();
        // Mask-keyed for the same reason as the image variant cache: story
        // flag writes (salon hair, spa) don't emit invalidation events.
        const cacheKey = `${baseName}|${activeMask}`;
        const cached = this._videoVariantCache.get(cacheKey);
        if (cached !== undefined) return cached;

        if (activeMask === 0) {
            this._videoVariantCache.set(cacheKey, baseName);
            return baseName;
        }

        for (let mask = activeMask; mask > 0; mask = this._nextSubMask(mask, activeMask)) {
            const candidate = this.buildVariantName(baseName, mask);
            if (this._knownVideos?.has(candidate)) {
                this._videoVariantCache.set(cacheKey, candidate);
                return candidate;
            }
        }

        this._videoVariantCache.set(cacheKey, baseName);
        return baseName;
    }

    /**
     * Resolve a full image URL path, searching through image folders.
     * Caches found paths for subsequent lookups.
     * @param {string} name - image name without extension
     * @returns {Promise<string|null>} URL or null if not found
     */
    async resolveImagePath(name) {
        if (!name) return null;

        // Apply variant resolution FIRST — the path cache is keyed by the
        // RESOLVED variant, so a story flag change between knots (salon hair,
        // spa) can't serve a stale URL for the same base name. The _masc axis
        // is resolved inside resolveVariantName → _buildActiveMask
        // (clothing-driven, fresh from Ink), superseding the old stat-driven
        // Fem<25 _masc probe that used to live here.
        const variantName = this.resolveVariantName(name);

        // Check cache (per resolved variant)
        const cached = this._imagePathCache.get(variantName);
        if (cached !== undefined) return cached;

        let candidates = [variantName];

        // Try each candidate in each folder
        for (const candidate of candidates) {
            for (const folder of IMAGE_FOLDERS) {
                // Try common extensions
                for (const ext of ['png', 'jpg', 'webp']) {
                    const url = `assets/images/${folder}/${candidate}.${ext}`;
                    try {
                        const resp = await fetch(url, { method: 'HEAD' });
                        if (resp.ok) {
                            this._imagePathCache.set(variantName, url);
                            this._knownImages.add(candidate);
                            return url;
                        }
                    } catch(e) {
                        // Network error — continue
                    }
                }
            }
        }

        // Not found — register as variant miss to avoid re-trying
        this._imagePathCache.set(variantName, null);
        this._variantMissCache.add(variantName);
        return null;
    }

    /**
     * Resolve a video URL path.
     * @param {string} name - video name without extension
     * @returns {string} URL
     */
    resolveVideoUrl(name) {
        if (!name) return null;
        const variantName = this.resolveVideoVariantName(name);
        return `assets/videos/${variantName}.mp4`;
    }

    /**
     * Register a known image name (call when images are discovered/loaded).
     */
    registerKnownImage(name) {
        this._knownImages.add(name);
    }

    /**
     * Load video manifest (list of known video filenames).
     * @param {string} url - path to VideoManifest.txt
     */
    async loadVideoManifest(url = 'assets/data/VideoManifest.txt') {
        this._knownVideos = new Set();
        try {
            const resp = await fetchWithEdgeRetry(url);
            const text = await resp.text();
            for (const line of text.split('\n')) {
                const trimmed = line.trim();
                if (!trimmed) continue;
                // Strip `.mp4` extension — `buildVariantName` produces bare names
                // (e.g. `bath_solo_climax_chastity`), and `resolveVideoVariantName`
                // checks `_knownVideos.has(candidate)`. Without stripping, lookups
                // always miss because the Set entries carry the extension.
                const bare = trimmed.endsWith('.mp4') ? trimmed.slice(0, -4) : trimmed;
                this._knownVideos.add(bare);
            }
            console.log(`[Appearance] Loaded ${this._knownVideos.size} video manifest entries`);
        } catch (e) {
            console.warn('[Appearance] Video manifest not found:', e.message);
        }
    }

    /**
     * Bulk register known images (e.g. from an image manifest).
     * @param {string[]} names
     */
    registerKnownImages(names) {
        for (const name of names) {
            this._knownImages.add(name);
        }
    }

    /**
     * Register a variant name as missing (404). Prevents re-trying in resolveVariantName.
     * @param {string} name
     */
    registerVariantMiss(name) {
        this._variantMissCache.add(name);
    }

    /**
     * Known media names for a kind — images (manifest/discovered) or videos
     * (VideoManifest). Read-only consumers (gallery viewer) use this instead
     * of reaching into _knownImages / _knownVideos. May be empty before the
     * manifests load.
     * @param {boolean} isVideo
     * @returns {Set<string>}
     */
    getKnownNames(isVideo) {
        return (isVideo ? this._knownVideos : this._knownImages) || new Set();
    }

    // ---- State queries ----

    get isWearingChastity() {
        if (this._debugOverride && this._debugForceChastity) return true;
        return this._activeStates[0];
    }

    get isWearingPlug() {
        if (this._inkReader) {
            const val = this._inkReader('wearing_plug');
            return this._toBool(val);
        }
        return false;
    }

    get hasLongHair() {
        if (this._debugOverride && this._debugForceLongHair) return true;
        return this._activeStates[1];
    }

    // ---- Debug ----

    setDebugOverrides(enabled, forceChastity = false, forceLongHair = false) {
        this._debugOverride = enabled;
        this._debugForceChastity = forceChastity;
        this._debugForceLongHair = forceLongHair;
        this.invalidateCache();
    }

    // ---- Cache management ----

    invalidateCache() {
        this._variantCache.clear();
        this._videoVariantCache.clear();
        this._imagePathCache.clear();
    }

    // ---- Internal ----

    _onVariableChanged(name, value) {
        // Check if this variable affects appearance
        if (name === 'wearing_wig') {
            // Wig override: wearing_wig forces longhair
            const hasHair = this._activeStates[1]; // current has_long_hair
            this._activeStates[1] = hasHair || this._toBool(value);
            this.invalidateCache();
            return;
        }
        // apartment_nest_tier change → invalidate cache so next resolve picks
        // up the new tier suffix for apartment_/master_visit_ names.
        if (name === 'apartment_nest_tier') {
            this.invalidateCache();
            return;
        }

        for (let i = 0; i < SUFFIX_DEFINITIONS.length; i++) {
            if (SUFFIX_DEFINITIONS[i].inkVariable === name) {
                const newState = this._toBool(value);
                if (this._activeStates[i] !== newState) {
                    this._activeStates[i] = newState;
                    // Re-apply wig override for longhair
                    if (i === 1 && this._inkReader) {
                        const wigVal = this._inkReader('wearing_wig');
                        if (this._toBool(wigVal)) this._activeStates[1] = true;
                    }
                    this.invalidateCache();
                }
                break;
            }
        }
    }

    _applyWigOverride(readVar) {
        try {
            const wigVal = readVar('wearing_wig');
            if (this._toBool(wigVal)) {
                this._activeStates[1] = true; // longhair index
            }
        } catch(e) {
            // Variable may not exist
        }
    }

    _buildActiveMask() {
        if (this._debugOverride) {
            let mask = 0;
            if (this._debugForceChastity) mask |= (1 << 0);
            if (this._debugForceLongHair) mask |= (1 << 1);
            return mask;
        }
        let mask = 0;
        for (let i = 0; i < SUFFIX_DEFINITIONS.length; i++) {
            if (this._activeStates[i]) mask |= (1 << i);
        }
        // Feminine axes (bits 0-2): read FRESH from Ink when a reader is
        // wired, overriding _activeStates. has_long_hair (salon) and
        // has_visible_breasts (spa) are STORY writes — they mutate during the
        // blockObservers window, so no FLAG_CHANGED event ever reaches
        // _activeStates (the event path only covers equipment-driven flags
        // like wearing_wig/wearing_chastity). _activeStates stays as the
        // no-reader fallback. Wig keeps forcing the longhair axis. Player
        // report 2026-06-05 ("still not having long hair in mirror").
        if (this._inkReader) {
            const fresh = (v) => {
                try { return this._toBool(this._inkReader(v)); } catch (e) { return false; }
            };
            mask = fresh('wearing_chastity') ? (mask | (1 << 0)) : (mask & ~(1 << 0));
            const longhair = fresh('has_long_hair') || fresh('wearing_wig');
            mask = longhair ? (mask | (1 << 1)) : (mask & ~(1 << 1));
            mask = fresh('has_visible_breasts') ? (mask | (1 << 2)) : (mask & ~(1 << 2));
        }
        // Masc axis (bit 3): read FRESH from Ink, overriding _activeStates. The
        // starter outfit equips before the ink pusher is wired (editor), so the
        // cold-start TRUE value never reaches _activeStates via an event.
        // presenting_masc lives in Ink (inventory keeps it = !EquippedFeminine)
        // and defaults true (Day-1 = male starter clothes).
        if (this._inkReader && this._toBool(this._inkReader('presenting_masc'))) {
            mask |= (1 << 3);
        } else {
            mask &= ~(1 << 3);
        }
        // male ⊥ boobs: a masculine-presenting render never shows breasts.
        // Drop the boobs bit (2) whenever masc (3) is set — same mechanism as
        // the nest-tier _maskWithoutBoobs drop. Centralised here so it covers
        // BOTH image (resolveVariantName) and video (resolveVideoVariantName).
        if (mask & (1 << 3)) mask &= ~(1 << 2);
        return mask;
    }

    /**
     * Public snapshot of the player's CURRENT appearance axes, fresh-read
     * from Ink (the same source resolveVariantName uses — §92-safe for
     * story-written flags like spa breasts / salon hair / cold-start masc).
     * Bit order: 0=chastity, 1=longhair, 2=boobs, 3=masc; masc⊥boobs is
     * already applied. UI consumers (gallery viewer defaults) read this
     * instead of reaching into _activeStates.
     */
    getActiveMask() {
        return this._buildActiveMask();
    }

    // Drop the boobs bit (index 2) from a mask — used for nest-aware names
    // where the boobs axis is intentionally skipped to keep under 8-variant cap.
    _maskWithoutBoobs(mask) {
        return mask & ~(1 << 2);
    }

    // Returns apartment_nest_tier for names matching NEST_PREFIXES, else 0.
    _getNestTierForName(baseName) {
        if (!this._inkReader) return 0;
        for (const prefix of NEST_PREFIXES) {
            if (baseName.startsWith(prefix)) {
                const v = this._inkReader('apartment_nest_tier');
                const n = Number(v) | 0;
                if (n < 0) return 0;
                if (n > 3) return 3;
                return n;
            }
        }
        return 0;
    }

    // ---- Variant-name building blocks (shared ladder API) ----
    // gallery.js (viewer toggles) + character-panel.js (nude-video chain)
    // consume these instead of re-implementing the suffix grammar. Bit order
    // everywhere: 0=chastity, 1=longhair, 2=boobs, 3=masc.

    /**
     * Append axis suffixes for the bits set in `mask`, in canonical order
     * (chastity → longhair → boobs → masc). Does NOT apply masc⊥boobs —
     * callers pass an already-sanitised mask (see _buildActiveMask /
     * getActiveMask).
     * @param {string} baseName
     * @param {number} mask - axis bitmask
     * @returns {string}
     */
    buildVariantName(baseName, mask) {
        let name = baseName;
        for (let i = 0; i < SUFFIX_DEFINITIONS.length; i++) {
            if ((mask & (1 << i)) !== 0) {
                name += SUFFIX_DEFINITIONS[i].imageSuffix;
            }
        }
        return name;
    }

    /**
     * Strip an ordered-axis variant suffix tail (e.g. `_chastity_longhair`)
     * from a name, returning the base. Only a tail that fullmatches the
     * grammar is stripped — non-axis tails (e.g. `foo_climax_chastity`)
     * lose only their valid trailing part (`_chastity`), matching the old
     * gallery literal-list behaviour.
     * @param {string} name
     * @returns {string}
     */
    stripVariantSuffix(name) {
        if (!name) return name;
        return name.replace(VARIANT_TAIL_STRIP_RE, '');
    }

    /**
     * Parse a variant suffix tail (leading underscore included, e.g.
     * `_longhair_masc`) against the ordered-axis grammar. Returns the axis
     * bitmask (0 for an empty tail), or -1 when the tail is NOT a pure
     * ordered-axis tail — sibling-prefix names like `_climax_chastity`
     * must not count as variants of a shorter base.
     * @param {string} tail
     * @returns {number} axis bitmask, or -1 on no-match
     */
    parseVariantTail(tail) {
        const m = typeof tail === 'string' ? tail.match(VARIANT_TAIL_FULL_RE) : null;
        if (!m) return -1;
        let mask = 0;
        for (let i = 0; i < SUFFIX_DEFINITIONS.length; i++) {
            if (m[i + 1]) mask |= (1 << i);
        }
        return mask;
    }

    /**
     * Iterate submasks of activeMask in descending order (most bits first).
     */
    _nextSubMask(current, activeMask) {
        return (current - 1) & activeMask;
    }

    _toBool(val) {
        if (typeof val === 'boolean') return val;
        if (typeof val === 'number') return val !== 0;
        return false;
    }
}

export const appearanceState = new AppearanceState();
